<template>
  <VcLayout
    class="TopBarActions top-bar-actions-container"
    :class="{ 'top-bar-actions-container--search-active': isSearchActive }"
    :data-qa="dataQa">
    <VcLayout class="topBarSection leftSection">
      <!-- Back button when search is expanded -->
      <VcLayout v-show="isSearchActive" class="backButtonSection">
        <VcButton
          v-if="!isLoading"
          ghost
          :color="isWhiteColor(textColor) ? 'white' : 'black'"
          class="backButton"
          :data-qa="`${dataQa}-backButton`"
          @click="onSearchBackClick">
          <template #prepend>
            <VcIcon :color="textColor" size="var(--font-size-small2)">
              {{ $vuetify.rtl ? '$chevron_right' : '$chevron_left' }}
            </VcIcon>
          </template>
          <span class="backButtonLabel"> {{ $t('layout.search_expanded_label') }} </span>
        </VcButton>
      </VcLayout>

      <!-- Expanded search: only when search is expanded (icon-only mode) -->
      <div
        v-if="!isLoading"
        class="expandedSearchSection"
        :class="{ 'expandedSearchSection--open': isSearchActive }">
        <VcLayout class="expandedSearchSection__inner" align-center>
          <VcSearchLayout
            :data-qa="dataQa"
            :value="!!query"
            :isSearch="isSearchActive"
            :label="searchPlaceholder"
            :placeholder="searchPlaceholder"
            :empty-state-title="emptyStateTitle"
            :empty-state-subtitle="$t('layout.search_empty_state.subtitle')"
            :empty-state-image="searchBarEmptyStateImage"
            :itemGroups="displaySearchResults"
            :throttle-time="800"
            @onSearch="onSearch"
            @onSeeAllClicked="onSeeAllClicked"
            @onItemClicked="onSearchItemClicked"
            @onDropdownChange="onDropdownChange"/>
        </VcLayout>
      </div>
    </VcLayout>

    <VcLayout class="topBarSection rightSection" flex-wrap justify-end>
      <!-- Search icon when not expanded -->
      <template v-if="!isSearchActive && !isLoading">
        <VcButton
          ghost
          class="d-flex specialButtonHeight search-icon-button"
          flavor="secondary"
          :data-qa="`${dataQa}-searchIcon`"
          :width="32"
          @click="onSearchIconClick">
          <template #prepend>
            <VcIcon :color="textColor" size="20"> $magnify_glass </VcIcon>
          </template>
        </VcButton>
      </template>

      <!-- BizAI (built-in) -->
      <VcButton
        v-if="showBizAi"
        class="bizai-header-btn specialButtonHeight"
        data-qa="bizai-button"
        flavor="secondary"
        ghost
        @click="onBizAiClick">
        <template #prepend>
          <BizAIIcon :color="textColor" />
        </template>
      </VcButton>

      <!-- Referral (built-in) -->
      <VcButton
        v-if="showReferral"
        class="impact-referral-btn specialButtonHeight"
        data-qa="referral-button"
        flavor="secondary"
        ghost
        icon
        @click="onReferralClick">
        <template #prepend>
          <VcIcon :color="textColor" size="16">icon-gift-16</VcIcon>
        </template>
      </VcButton>

      <!-- Notifications -->
      <template v-if="showNotifications">
        <VcSkeleton v-if="isLoading" type="avatar" />
        <VcDropdown
          v-else
          :value="isPaneOpen"
          max-height="700px"
          :data-qa="`${dataQa}-notificationsPaneDropdown`"
          @input="toggleNotificationsPane">
          <template #activator="{ on, attrs }">
            <VcBadge
              v-bind="attrs"
              v-on="on"
              :offset-x="15"
              :offset-y="15"
              :badge-text="notificationsBadgeValue"
              :data-qa="`${dataQa}-notificationsBadge`">
              <VcButton
                ghost
                class="d-flex specialButtonHeight notifications-button"
                flavor="secondary"
                :data-qa="`${dataQa}-notifications`"
                :width="32"
                @click="onNotificationsBadgeClicked">
                <template #prepend>
                  <VcIcon :color="textColor" size="20" class="notifications-icon"> $notifications </VcIcon>
                </template>
              </VcButton>
            </VcBadge>
          </template>
          <VcLayout column>
            <NotificationPaneTitle @notificationsSettingsButtonClick="openNotificationsSettings" />
            <NotificationPane
              class="notifications-pane-wrapper"
              @onNotificationRowClicked="closeNotificationsPane"
            />
          </VcLayout>
        </VcDropdown>
      </template>

      <!-- Help -->
      <template v-if="showHelpDropdown">
        <VcSkeleton v-if="isLoading" type="avatar" />
        <VcDropdown v-else :data-qa="`${dataQa}-helpMenu`">
          <template #activator="{ on, attrs }">
            <VcButton
              class="helpButton specialButtonHeight helpButton--withLabel"
              flavor="secondary"
              ghost
              :data-qa="`${dataQa}-help`"
              v-bind="attrs"
              @click="onHelpButtonClicked"
              v-on="on">
              <template #prepend>
                <VcIcon :color="textColor" size="20"> $help </VcIcon>
              </template>
              <template #default>
                <div class="helpButton--label">
                  {{ helpButtonLabel }}
                </div>
              </template>
            </VcButton>
          </template>
          <VcList
            v-if="helpMenuItems && helpMenuItems.length > 0"
            :items="helpMenuItems"
            :data-qa="`${dataQa}-helpMenuList`"
            @select="(value) => onHelpItemClicked(helpMenuItems.find((item) => item.identifier === value))">
            <template #default="item">
              <span class="helpMenuItem">{{ item.label }}</span>
            </template>
          </VcList>
        </VcDropdown>
      </template>

      <!-- Upgrade -->
      <VcSkeleton v-if="isLoading" type="avatar" />
      <VcButton
        v-else-if="upgradeButtonLabel"
        outlined
        small
        class="upgradeButton specialButtonHeight upgradeButton--withLabel"
        :data-qa="`${dataQa}-upgrade`"
        @click="onUpgradeButtonClicked">
        <template #prepend>
          <VcIcon :color="textColor"> $upgrade </VcIcon>
        </template>
        <span class="upgradeButton--label">{{ upgradeButtonLabel }}</span>
      </VcButton>

      <!-- Account (always shown) -->
      <VcSkeleton v-if="isLoading" type="avatar" />
      <VcAccountMenu
        v-else
        v-model="isAccountMenuOpen"
        :items="accountMenuItems"
        :color-id="colorId"
        :email="email"
        :name="name"
        :image-path="imagePath"
        :logout-label="logoutLabel"
        :logout-icon="logoutIcon"
        :show-logout="showLogout"
        :data-qa="`${dataQa}-accountMenu`"
        @input="onAccountMenuChange"
        @click="onAccountMenuClick">
        <template #activator="{ on, attrs }">
          <VcButton
            v-bind="attrs"
            v-on="on"
            flavor="secondary"
            ghost
            class="accountMenuButton specialButtonHeight accountMenuButton--withLabel"
            :data-qa="`${dataQa}-account`"
            @click="onAccountButtonClick">
            <template #prepend>
              <VcAvatar
                :name="name"
                :color-id="colorId"
                :image-path="imagePath"
                size="x-sm"
                :data-qa="`${dataQa}-accountAvatar`"/>
            </template>
            <template #default>
              <div class="accountName">
                {{ name }}
              </div>
            </template>
            <template #append>
              <VcIcon :color="textColor" small> $chevron_down </VcIcon>
            </template>
          </VcButton>
        </template>
        <template #footer>
          <PoweredByContainer show-divider text-color="black"/>
        </template>
      </VcAccountMenu>
    </VcLayout>
  </VcLayout>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import LayoutConstants from '@/constansts/layoutConstants';
import outgoingPostMessageUtils from '@/utils/eventCommunicationUtils/outgoingPostMessageUtils';
import SearchBarConstants from '@/constansts/SearchBarConstants';
import MenuItemsUtils from '@/modules/layout/mainMenu/utils/menuItemsUtils';
import BizAIIcon from '@/modules/bizai/components/BizAiIcon.vue';
import NotificationPane from '@/modules/notificationCenter/components/notificationPane/NotificationPane.vue';
import NotificationPaneTitle from '@/modules/notificationCenter/components/notificationPaneTitle/notificationPaneTitle.vue';
import PoweredByContainer from '@/modules/layout/mainMenu/components/PoweredByContainer.vue';
import isWhiteColor from '../utils/colorUtils';

export default {
  name: 'TopBarActions',
  inheritAttrs: false,
  components: {
    BizAIIcon,
    NotificationPane,
    NotificationPaneTitle,
    PoweredByContainer,
  },
  props: {
    isSearchActive: {
      type: Boolean,
      default: false,
    },
    showLogout: {
      type: Boolean,
      default: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    dataQa: {
      type: String,
      default: 'TopBarActions',
    },
    showNotifications: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    isAccountMenuOpen: false,
    query: '',
    isPaneOpen: false,
  }),
  computed: {
    ...mapGetters('StaffStore', ['staffData', 'hasPermission']),
    ...mapGetters('BusinessStore', [
      'attributes',
      'directorySettings',
      'isPartnerDirectory',
      'hasHelpLinks',
      'textColor',
    ]),
    ...mapGetters('NotificationCenterStore', ['isNewCounter']),
    ...mapGetters('SearchBarStore', ['searchResults']),
    searchPlaceholder() {
      return this.$t('layout.search_placeholder');
    },
    emptyStateTitle() {
      return this.$t('layout.search_empty_state.title', { query: this.query });
    },
    searchBarEmptyStateImage() {
      return require('@/assets/images/search-bar/search-bar-empty-state.svg');
    },
    userProfileDetails() {
      return {
        imagePath: this.staffData?.photo_path || this.attributes?.photo_path || '',
        email: this.staffData?.email || '',
        displayName: this.staffData?.display_name || '',
        colorId: this.staffData?.color_id || 27,
      };
    },
    name() {
      return this.userProfileDetails.displayName;
    },
    email() {
      return this.userProfileDetails.email;
    },
    colorId() {
      return this.userProfileDetails.colorId;
    },
    imagePath() {
      return this.userProfileDetails.imagePath;
    },
    myAccountMenuWideFF() {
      return this.$hasFeature('my_account_menu_wide');
    },
    newProfilePageFF() {
      return this.$hasFeature('new_profile_page');
    },
    isTealiumEnabled() {
      return this.$hasFeature('thryv_tealium_script') && this.$hasFeature('thryv_command_bar');
    },
    shouldDisplayHelpDropDown() {
      return this.isPartnerDirectory && !this.isTealiumEnabled && this.hasHelpLinks;
    },
    showHelpDropdown() {
      return this.shouldDisplayHelpDropDown;
    },
    accountMenuItems() {
      const myAccountRout = this.myAccountMenuWideFF ? 'app.setting_menu' : 'app.settings_page';
      const profileRoute = this.newProfilePageFF ? 'staffProfile' : 'app.business_info_settings';
      const profileRouteType = this.newProfilePageFF
        ? LayoutConstants.ROUTING_TYPES.pov
        : LayoutConstants.ROUTING_TYPES.angular;
      const settingsPermission = this.newProfilePageFF ? null : 'can_access_settings';

      const items = [
        {
          label: this.$t('common.navigation.account'),
          icon: 'icon-user',
          event: 'my-account',
          roles: ['admin', 'manager', 'marketing'],
          permission: 'can_access_settings',
          routingType: LayoutConstants.ROUTING_TYPES.angular,
          route: myAccountRout,
          routeData: { page: 'my_account' },
        },
        {
          label: this.$t('common.navigation.profile'),
          icon: 'icon-Suitcase',
          event: 'business-info',
          roles: ['admin', 'manager', 'marketing'],
          permission: 'can_access_settings',
          routingType: profileRouteType,
          route: profileRoute,
        },
        {
          label: this.$t('common.navigation.settings'),
          icon: 'icon-Settings',
          event: 'settings',
          roles: ['admin', 'manager', 'marketing'],
          permission: settingsPermission,
          routingType: LayoutConstants.ROUTING_TYPES.angular,
          route: 'app.settings',
          routeData: {},
        },
        {
          label: this.$t('common.navigation.staff'),
          icon: 'icon-User-vneck',
          event: 'staff',
          roles: ['admin'],
          permission: 'can_access_staff_management',
          routingType: LayoutConstants.ROUTING_TYPES.angular,
          route: 'app.settings_page',
          routeData: { page: 'staff' },
        },
      ];

      if (!this.$hasFeature('pkg.business_administration.getting_started.deny')) {
        items.push({
          label: this.$t('onboarding.new_onboarding.quick_btn_title'),
          icon: 'icon-Rocket_new',
          event: 'getting-started',
          roles: ['admin'],
          permission: 'can_access_admin_account',
          action: 'openChecklist',
        });
      }

      if (this.$hasFeature('roles_and_permissions')) {
        return items.filter((item) => (item.permission ? this.hasPermission(item.permission) : true));
      }
      const staffRole = this.staffData?.role || '';
      return items.filter((item) => item.roles.includes(staffRole));
    },
    upgradeButtonLabel() {
      return this.attributes?.is_upgradeable ? this.$t('common.words.upgrade') : '';
    },
    helpButtonLabel() {
      return this.$t('common.navigation.help_button');
    },
    logoutLabel() {
      return this.$t('common.navigation.logout');
    },
    logoutIcon() {
      return 'icon-Logout_POV';
    },
    helpMenuItems() {
      const dropDownDetails = [
        {
          identifier: 0,
          event: 'help-dropdown-support',
          label: this.$t('common.navigation.support'),
          url: this.directorySettings?.custom_support_url,
        },
        {
          identifier: 1,
          event: 'help-dropdown-faq',
          label: this.$t('common.navigation.general_faq'),
          url: this.directorySettings?.faq_url,
        },
        {
          identifier: 2,
          event: 'schedule-with-cs',
          label: this.$t('common.help.schedule_with_cs'),
          url: this.directorySettings?.help_links?.schedule_with_cs,
        },
        {
          identifier: 3,
          event: 'submit-support-req',
          label: this.$t('common.help.submit_support_req'),
          url: this.directorySettings?.help_links?.submit_support_req,
        },
      ];
      return this.shouldDisplayHelpDropDown ? dropDownDetails.filter((item) => !!item.url) : [];
    },
    notificationsBadgeValue() {
      return this.isNewCounter > 99 ? '+99' : this.isNewCounter;
    },
    showBizAi() {
      return this.$hasFeature('bizai_chat');
    },
    showReferral() {
      return this.$hasFeature('pkg.thryv.referral_widget');
    },
    displaySearchResults() {
      return (this.searchResults || []).map((itemGroup) => ({
        ...itemGroup,
        label: `${this.$t(`layout.search_categories.${itemGroup.id}`)} (${itemGroup.count})`,
        items: itemGroup.items.map((item) => {
          const searchItem = {
            ...item,
            name:
              item.type !== 'client' && item.type !== 'document'
                ? this.$t(`layout.result_names.${item.type}`)
                : item.name,
          };
          if (itemGroup.id === 'client') {
            searchItem.id = item.matter_uid;
            searchItem.contact_uid = item.id;
          }
          return searchItem;
        }),
      }));
    },
  },
  mounted() {
    this.$root.$on('overlay-clicked', this.onOverlayClicked);
  },
  beforeDestroy() {
    this.$root.$off('overlay-clicked', this.onOverlayClicked);
  },
  methods: {
    isWhiteColor,
    ...mapActions('SearchBarStore', ['search', 'setShowSearchResults']),
    ...mapActions('AngularStore', ['setIsOverlayDisplay', 'toggleIsOverlayDisplay']),
    ...mapActions('AuthStore', ['onLogout']),
    ...mapActions('NotificationCenterStore', ['handleTopbarBadgeClicked']),
    ...mapActions('BizaiStore', ['toggleVisibility']),
    ...mapActions('ModalStore', ['setForceModalMode']),
    onOverlayClicked() {
      this.setShowSearchResults(false);
      this.closeNotificationsPane();
    },
    onSearch(query) {
      this.query = query;
      if (query !== '') this.$track('Top bar search typed', { queryText: query });
      this.search(query);
    },
    onSearchItemClicked(entity) {
      if (entity.showMore) {
        this.onSeeAllClicked(entity.type);
      } else {
        const data = {
          route: SearchBarConstants.ENTITY_STATES[entity.type],
          data: { uid: entity.type === 'client' ? entity.contact_uid : entity.id },
        };
        if (entity.matter_uid) data.data.matter_uid = entity.matter_uid;
        outgoingPostMessageUtils.post(LayoutConstants.NAVIGATE_TO, data);
      }
      this.query = '';
      this.setShowSearchResults(false);
    },
    onSeeAllClicked(entityType) {
      outgoingPostMessageUtils.post('navigate-to', {
        route: 'app.search',
        data: { type: entityType, query: this.query },
      });
      this.setShowSearchResults(false);
    },
    onDropdownChange() {
      this.setShowSearchResults(false);
    },
    onSearchIconClick() {
      this.setShowSearchResults(true);
      this.focusSearchInput();
    },
    focusSearchInput() {
      this.$nextTick(() => {
        const input = document.querySelector('input[type="search"]');
        input?.focus();
      });
    },
    onSearchBackClick() {
      this.setShowSearchResults(false);
    },
    onUpgradeButtonClicked() {
      outgoingPostMessageUtils.post(LayoutConstants.FIRE_ACTION, {
        event_name: 'top-bar-upgrade-button-clicked',
      });
    },
    onHelpButtonClicked() {
      if (this.isTealiumEnabled) {
        window.CommandBar?.toggleHelpHub?.();
      } else if (!this.shouldDisplayHelpDropDown && !this.directorySettings?.is_whitelabel) {
        this.$track('Help Click', { from: 'Top bar' });
        window.open(
          `https://support.vcita.com/hc/${this.attributes?.locale?.toLowerCase() || 'en'}/signin`,
          '_blank',
        );
      }
    },
    onHelpItemClicked(item) {
      if (item?.url) window.open(item.url, '_blank');
    },
    onBizAiClick() {
      this.toggleVisibility();
    },
    onReferralClick() {
      if (window.utag) {
        window.utag.link({ tealium_event: 'open-referral-modal' });
      }
    },
    onAccountButtonClick() {
      this.toggleIsOverlayDisplay();
      this.isAccountMenuOpen = !this.isAccountMenuOpen;
    },
    onAccountMenuChange() {
      this.setIsOverlayDisplay(false);
    },
    onAccountMenuClick(value) {
      this.handleAccountMenuItemClick(value);
      this.isAccountMenuOpen = !this.isAccountMenuOpen;
    },
    handleAccountMenuItemClick(value) {
      if (value === 'logout') {
        this.onLogout();
        return;
      }
      // VcAccountMenu may pass the event string (e.g. "my-account"); resolve to full item from accountMenuItems
      const selectedItem = typeof value === 'object' && value !== null
        ? value
        : this.accountMenuItems.find((item) => item.event === value) || null;
      if (!selectedItem) return;
      this.setIsOverlayDisplay(false);
      this.handleAccountMenuItemEvent(selectedItem);
    },
    handleAccountMenuItemEvent(selectedItem) {
      const isAngularDialog = selectedItem.event === 'getting-started';
      const isPovPage = !this.$route.meta?.isAngular;
      if (isAngularDialog && isPovPage) {
        this.setForceModalMode(true);
      }
      if (selectedItem.action) {
        this.fireAccountMenuItemAction(selectedItem, isAngularDialog && isPovPage);
      } else {
        this.handleAppRouting(selectedItem);
      }
    },
    handleAppRouting(selectedItem) {
      if (selectedItem.routingType === LayoutConstants.ROUTING_TYPES.angular) {
        this.angularRouting(selectedItem);
      } else if (selectedItem.routingType === LayoutConstants.ROUTING_TYPES.pov) {
        this.$router.push({ name: selectedItem.route, params: selectedItem.routeData || {} });
      }
    },
    angularRouting(selectedItem) {
      outgoingPostMessageUtils.post(LayoutConstants.NAVIGATE_TO, {
        route: selectedItem.route,
        data: selectedItem.routeData,
      });
    },
    fireAccountMenuItemAction(item, hideBackground = false) {
      MenuItemsUtils.fireAction(item, hideBackground);
    },
    toggleNotificationsPane() {
      this.isPaneOpen = !this.isPaneOpen;
      this.toggleIsOverlayDisplay();
    },
    closeNotificationsPane() {
      this.isPaneOpen = false;
      this.setIsOverlayDisplay(false);
    },
    openNotificationsSettings() {
      this.closeNotificationsPane();
      outgoingPostMessageUtils.post(LayoutConstants.NAVIGATE_TO, {
        route: 'app.notification_settings',
      });
    },
    async onNotificationsBadgeClicked() {
      this.toggleNotificationsPane();
      await this.handleTopbarBadgeClicked();
    },
  },
};
</script>

<style lang="scss" scoped>
.TopBarActions.top-bar-actions-container {
  container-type: normal;
  container-name: top-bar-actions;
  column-gap: var(--size-value1);
  justify-content: flex-end;
  width: auto;
  background-color: transparent;
  color: var(--v-text-base);
  padding: 0;
  height: 100%;

  .accountMenuButton {
    padding: 0 var(--size-value2);
  }

  .accountName {
    padding-inline-start: var(--size-value2);
    color: var(--brand-text-color);
  }

  .specialButtonHeight {
    height: var(--size-value8);
  }

  &.top-bar-actions-container--search-active {
    width: 100%;
    min-width: 0;
  }
}

.topBarSection {
  align-items: center;
}

/* When search not active: left section (search container) gets 0 width */
.leftSection {
  flex: 0 0 0;
  min-width: 0;
  overflow: hidden;
  transition: min-width 0.5s ease;
  justify-content: flex-end;
}

/* When search active: left section grows for expanded search + animation */
.top-bar-actions-container--search-active .leftSection {
  flex: 1 1 0;
}

.rightSection {
  flex: 0 0 auto;
  gap: var(--size-value2);
}

::v-deep {
  .v-badge__badge {
    font-weight: var(--font-weight-medium2);
  }
}

/* Help button: label visible via container query */
.helpButton--label {
  padding-inline-start: var(--size-value2);
}
// todo: check how to move it to window width because this container will never get to this width
@container top-bar-actions (max-width: 1199px) {
  .helpButton--withLabel .helpButton--label {
    display: none;
  }
}

@container top-bar-actions (min-width: 1200px) {
  .helpButton--withLabel {
    font-weight: var(--font-weight-medium2);
    padding: 0 var(--size-value3);
  }
}

.helpMenuItem {
  font-size: 13px;
  font-weight: var(--font-weight-medium);
}

@container top-bar-actions (max-width: 1039px) {
  .accountMenuButton--withLabel .accountName {
    display: none;
  }
}

@container top-bar-actions (min-width: 1040px) {
  .accountName {
    font-size: 12px;
    font-weight: var(--font-weight-medium2);
    line-height: var(--size-value6);
    padding-inline-start: var(--size-value2);
    text-overflow: ellipsis;
    max-width: 150px;
    white-space: nowrap;
    overflow: hidden;
  }
}

.upgradeButton--withLabel {
  padding: 0 var(--size-value1);
}

.upgradeButton--label {
  display: inline;
}

@container top-bar-actions (max-width: 783px) {
  .upgradeButton--withLabel .upgradeButton--label {
    display: none;
  }
}

.backButtonSection {
  margin-inline-start: var(--size-value3);
  align-items: center;
}

.backButton {
  color: inherit !important;
}

.expandedSearchSection {
  width: 0;
  opacity: 0;
  transition: width 0.5s ease, opacity 0.2s ease;

  &--open {
    width: 100%;
    max-width: 560px;
    opacity: 1;
    margin-inline-end: var(--size-value1);
  }

  &__inner {
    gap: var(--size-value2);
  }
}

.backButtonLabel {
  font-size: var(--font-size-x-small);
  font-weight: var(--font-weight-medium2);
  margin-inline-start: var(--size-value1);
  color: inherit;
  line-height: var(--size-value6);

  &:hover {
    text-decoration: underline;
  }
}

.rightSection .notifications-button {
  padding: 0;
}

.notifications-pane-wrapper {
  width: 480px;
  min-height: 602px;
  height: 602px;
}

.bizai-header-btn {
  width: 32px;
  padding: 6px !important;
}
</style>
